import {User} from 'src/app/shared/models/user';
import {Mission} from '../../common-mission/models/mission';
import {Customer} from 'src/app/admin/modules/admin-customer/models/customer';

export interface Anomaly {
    id: string;
    type: AnomalyType;
    subtype: AnomalySubtype;
    stage: AnomalyStage;
    amountWithheld: number;
    validated: boolean;
    missingInfoValet: string[];
    missingInfoCustomer: string[];
    waitingFor: AnomalyWaitingFor[];
    complementaryInfos: any;
    mission: Mission;
    customer: Customer;
    valet: User;
    history: AnomalyHistory[];
    location: string;
    description: string;
    valetPrecision: string;
    valetResponsible: boolean;
    report: File;
    accidentReport: File;
    registrationCertificate: File;
    insuranceCertificate: File;
    quotations: File[];
    photos: File[];
    photosAccident: File[];
    otherFiles: File[];
    amount: number;
    contraventionMessage: string;
    breakdownLocation: string;
    breakdownReport: File;
    repairerName: string;
    assistanceFileNumber: string;
    garageName: string;
    speedRating: number;
    assistanceRating: number;
    clientMessage: string;
    fuelLevelFromUrl: string;
    fuelLevelToUrl: string;
    createdAt: Date;
    updatedAt: Date;
    isRolling: boolean;
}

export interface AnomalyHistory {
    id: string;
    description: string;
    user: HistoryUser;
    type: HistoryType;
    anomaly: Anomaly;
    createdAt: Date;
}

export enum AnomalyType {
    Accident = 'Sinistre',
    Breakdown = 'Panne',
    Behaviour = 'Comportement',
    Delay = 'Retard/Avance',
    Mileage = 'Kilometrage',
    Infraction = 'Contravention',
    Service = 'Service',
    Other = 'Autre'
}

export type AnomalySubtype =
  ValetAnomalySubtype |
  CustomerAnomalySubtype |
  PopValetAnomalySubtype |
  'old_anomaly';

export enum AnomalyCustomerStage {
    ToFill = 'Données à fournir',
    Closed = 'Cloturé',
    InProgress = 'En cours de traitement',
    Repair = 'En réparation'
}

export enum AnomalyStage {
    InCompletion = 'En complétion',
    ToBeProcessed = 'À traiter',
    Repair = 'En réparation',
    Closed = 'Cloturé',
    InProgress = 'En cours'
}

export enum AnomalyWaitingFor {
    Popvalet = 'Popvalet',
    Valet = 'Valet',
    Customer = 'Client'
}

export enum HistoryType {
    Closed = 'Closed',
    Modified = 'Modified',
    AskedInfo = 'Asked Info',
    StatusChanged = 'Status changed',
    Created = 'Created',
    Internal = 'Internal',
    Message = 'Message',
}

export enum HistoryUser {
    Customer = 'Client',
    Valet = 'Valet',
    Admin = 'Admin'
}

export enum AnomalyFields {
    ValetResponsible = 'valetResponsible',
    Location = 'location',
    Description = 'description',
    Amount = 'amount',
    Photos = 'photos',
    Report = 'report',
    Delay = 'delay',
    Mileage = 'mileage',
    AccidentReport = 'accidentReport',
    Quotations = 'quotations',
    ValetPrecision = 'valetPrecision',
    OtherFiles = 'otherFiles',
    RegistrationCertificate = 'registrationCertificate',
    InsuranceCertificate = 'insuranceCertificate',
    IsRolling = 'isRolling',
    BreakdownLocation = 'breakdownLocation',
    RepairerName = 'repairerName',
    AssistanceFileNumber = 'assistanceFileNumber',
    GarageName = 'garageName',
    AmountWithheld = 'amountWithheld',
    Validated = 'validated',
    FuelLevelFromUrl = 'fuelLevelFromUrl',
    FuelLevelToUrl = 'fuelLevelToUrl'
}

export enum ValetAnomalySubtype {
    SERVICE_OMISSION = 'valet.service.service_omission',
    SERVICE_NO_REFILL = 'valet.service.no_refill',
    MISSING_DOCUMENT = 'valet.missing_document',
    INVALID_DOCUMENT = 'valet.invalid_document',
    BEHAVIOUR = 'valet.behaviour',
    OUTFIT = 'valet.outfit',
    HIJACKING = 'valet.hijacking',
    AT_FAULT_ACCIDENT = 'valet.at_fault_accident',
    NOT_AT_FAULT_ACCIDENT = 'valet.not_at_fault_accident',
    VEHICLE_FAILURE = 'valet.vehicle_failure',
    WITHDRAWAL = 'valet.withdrawal',
    ABANDON = 'valet.abandon',
    MISSION_DEFERRAL = 'valet.mission_deferral',
    TRAFFIC_TICKET_LT_20 = 'valet.traffic_ticket.LT_20',
    TRAFFIC_TICKET_20_TO_40 = 'valet.traffic_ticket.20_to_40',
    TRAFFIC_TICKET_GT_40 = 'valet.traffic_ticket.GT_40',
    TRAFFIC_TICKET_UNPAID_TOLL = 'valet.traffic_ticket.unpaid_toll',
    TRAFFIC_TICKET_PARKING = 'valet.traffic_ticket.parking',
    TRAFFIC_TICKET_OLD = 'valet.traffic_ticket.old_anomaly',
    DELAY = 'valet.delay',
    IN_ADVANCE = 'valet.in_advance',
    MILEAGE = 'valet.excess_mileage',
    CUSTOMER_FINANCIAL = 'valet.customer_request.financial_solicitation',
    CUSTOMER_TRANSPORT = 'valet.customer_request.transport_request',
}

export enum CustomerAnomalySubtype {
    CONFLICTING_COMMUNICATION = 'customer.conflicting_communication',
    VEHICLE_WAITING = 'customer.vehicle_waiting',
    VEHICLE_NOT_READY = 'customer.vehicle_not_ready',
    VEHICLE_NOT_AVAILABLE = 'customer.vehicle_not_available',
    LATE_CANCELLATION = 'customer.late_cancellation',
    VEHICLE_FAILURE = 'customer.vehicle_failure',
    RESCHEDULING = 'customer.rescheduling',
    REQUEST = 'customer.request',
}

export enum PopValetAnomalySubtype {
    LATE_CANCELLATION = 'popvalet.late_cancellation',
    SCHEDULING_ERROR = 'popvalet.scheduling_error',
}

export const neededInfoValet: Record<AnomalyType, AnomalyFields[]> = {
    [AnomalyType.Accident]: [
      AnomalyFields.ValetPrecision,
      AnomalyFields.IsRolling,
      AnomalyFields.AssistanceFileNumber,
      AnomalyFields.GarageName,
      AnomalyFields.ValetResponsible
    ],
    [AnomalyType.Breakdown]: [
      AnomalyFields.Description,
      AnomalyFields.BreakdownLocation,
      AnomalyFields.RepairerName,
      AnomalyFields.AssistanceFileNumber,
      AnomalyFields.Location,
      AnomalyFields.IsRolling,
      AnomalyFields.ValetResponsible
    ],
    [AnomalyType.Infraction]: [
      AnomalyFields.ValetResponsible
    ],
    [AnomalyType.Behaviour]: [
      AnomalyFields.ValetResponsible
    ],
    [AnomalyType.Delay]: [],
    [AnomalyType.Mileage]: [],
    [AnomalyType.Service]: [],
    [AnomalyType.Other]: []
};

export const neededInfoCustomer: Record<AnomalyType, AnomalyFields[]> = {
    [AnomalyType.Accident]: [
      AnomalyFields.Location,
      AnomalyFields.Description,
      AnomalyFields.Photos,
      AnomalyFields.Quotations,
      AnomalyFields.RegistrationCertificate,
      AnomalyFields.InsuranceCertificate
    ],
    [AnomalyType.Breakdown]: [],
    [AnomalyType.Infraction]: [AnomalyFields.Photos, AnomalyFields.Amount, AnomalyFields.Description],
    [AnomalyType.Behaviour]: [AnomalyFields.Description],
    [AnomalyType.Delay]: [],
    [AnomalyType.Mileage]: [],
    [AnomalyType.Service]: [],
    [AnomalyType.Other]: []
};

export const showOtherInfosAdmin: Record<AnomalyType, AnomalyFields[]> = {
    [AnomalyType.Accident]: [],
    [AnomalyType.Breakdown]: [],
    [AnomalyType.Infraction]: [],
    [AnomalyType.Behaviour]: [],
    [AnomalyType.Delay]: [
        AnomalyFields.Delay,
        AnomalyFields.AmountWithheld,
        AnomalyFields.Validated
    ],
    [AnomalyType.Mileage]: [
        AnomalyFields.Mileage
    ],
    [AnomalyType.Service]: [
        AnomalyFields.FuelLevelFromUrl,
        AnomalyFields.FuelLevelToUrl,
        AnomalyFields.Validated
    ],
    [AnomalyType.Other]: [
        AnomalyFields.AmountWithheld,
        AnomalyFields.Validated
    ]
};
